// Building grid labels, drawn on ONE 2D canvas over the viewer.
//
// They used to be CSS2DObjects: a DOM node per end of every grid line,
// placed by CSS2DRenderer - which walks the WHOLE scene graph every frame
// to find them (7k pool meshes on SpaceX). That walk, not the lines, is
// what made the grid heavy; the lines were already one LineSegments draw.
//
// And a label pinned to a line's END leaves the screen the moment you zoom
// in past that end, so the name you need is never where you are looking
// (owner, 2026-09-22: "the labels don't drift down with their lines").
// Here every line is clipped to the screen first and its labels sit at the
// ends of the part you can SEE: at the real end when it is on screen, at
// the screen edge when it is not, so each label slides along its own line
// and a line on screen always shows its name.
//
// This file is pure arithmetic (no three, no DOM) so it can be tested.

// A world point into clip space. `m` is three's Matrix4.elements, which
// is COLUMN-major, hence the index pattern.
function toClip(m, p) {
  const { x, y, z } = p;
  return {
    x: m[0] * x + m[4] * y + m[8] * z + m[12],
    y: m[1] * x + m[5] * y + m[9] * z + m[13],
    z: m[2] * x + m[6] * y + m[10] * z + m[14],
    w: m[3] * x + m[7] * y + m[11] * z + m[15],
  };
}

const lerp4 = (a, b, t) => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
  z: a.z + (b.z - a.z) * t,
  w: a.w + (b.w - a.w) * t,
});

// Liang-Barsky: the part of segment p->q inside the rectangle, as the
// parameters [t0, t1] along it, or null when none of it is.
export function clipToRect(p, q, rect) {
  const dx = q.x - p.x;
  const dy = q.y - p.y;
  let t0 = 0;
  let t1 = 1;
  const edges = [
    [-dx, p.x - rect.x0],
    [dx, rect.x1 - p.x],
    [-dy, p.y - rect.y0],
    [dy, rect.y1 - p.y],
  ];
  for (const [pp, qq] of edges) {
    if (pp === 0) {
      if (qq < 0) return null;
    } else {
      const r = qq / pp;
      if (pp < 0) { if (r > t1) return null; if (r > t0) t0 = r; } else { if (r < t0) return null; if (r < t1) t1 = r; }
    }
  }
  return [t0, t1];
}

/**
 * Where each grid line's labels go on screen this frame.
 *
 * lines:    [{ text, a: {x,y,z}, b: {x,y,z} }] in world space
 * viewProj: Matrix4.elements of projection x view (camera.projectionMatrix
 *           times camera.matrixWorldInverse)
 * width, height: the canvas size in CSS pixels
 * radius:   half the label's size; labels are kept that far inside the edge
 *
 * Returns [{ text, x, y }] in CSS pixels. A line entirely off screen or
 * behind the camera gets no label - there is nothing of it to name.
 */
export function placeGridLabels(lines, viewProj, width, height, { radius = 11, margin = 3 } = {}) {
  const out = [];
  if (!lines || !lines.length || !(width > 0) || !(height > 0)) return out;
  const inset = radius + margin;
  const rect = { x0: inset, y0: inset, x1: width - inset, y1: height - inset };
  if (rect.x1 <= rect.x0 || rect.y1 <= rect.y0) return out;
  for (const line of lines) {
    let ca = toClip(viewProj, line.a);
    let cb = toClip(viewProj, line.b);
    // Near plane first, in clip space (z >= -w): a point behind the camera
    // projects to the mirror side of the screen, and drawing a label there
    // is worse than drawing none.
    const da = ca.z + ca.w;
    const db = cb.z + cb.w;
    if (da < 0 && db < 0) continue;
    let aCut = false;
    let bCut = false;
    if (da < 0) { ca = lerp4(ca, cb, da / (da - db)); aCut = true; }
    if (db < 0) { cb = lerp4(ca, cb, da / (da - db)); bCut = true; }
    if (!(ca.w > 1e-9) || !(cb.w > 1e-9)) continue;
    const sa = { x: (ca.x / ca.w * 0.5 + 0.5) * width, y: (0.5 - ca.y / ca.w * 0.5) * height };
    const sb = { x: (cb.x / cb.w * 0.5 + 0.5) * width, y: (0.5 - cb.y / cb.w * 0.5) * height };
    // Then the part of it inside the screen, kept one label's size in from
    // the edge so a label at the edge is drawn whole.
    const len = Math.hypot(sb.x - sa.x, sb.y - sa.y);
    if (!(len > 0)) continue;
    const ux = (sb.x - sa.x) / len;
    const uy = (sb.y - sa.y) / len;
    // Real ends are labelled one radius beyond the line, the way a Revit
    // bubble sits past the end rather than on top of the line.
    const pa = aCut ? sa : { x: sa.x - ux * radius, y: sa.y - uy * radius };
    const pb = bCut ? sb : { x: sb.x + ux * radius, y: sb.y + uy * radius };
    const t = clipToRect(pa, pb, rect);
    if (!t) continue;
    const at = (k) => ({ x: pa.x + (pb.x - pa.x) * k, y: pa.y + (pb.y - pa.y) * k });
    const e0 = at(t[0]);
    const e1 = at(t[1]);
    out.push({ text: line.text, x: e0.x, y: e0.y });
    // One label is enough when the visible stretch is shorter than two.
    if (Math.hypot(e1.x - e0.x, e1.y - e0.y) >= 2 * radius + 2) out.push({ text: line.text, x: e1.x, y: e1.y });
  }
  return out;
}
